import { Link } from "react-router-dom";
import { Lock, ArrowRight, Home } from "lucide-react";
import useBilling from "../hooks/useBilling";

function hasFeature(features, key) {
  if (!features) return false;
  const v = features[key];
  if (v == null) return false;
  if (typeof v === "number") return !!v;
  if (typeof v === "string") return !!v;
  return !!v; // truthy for objects/arrays
}

export default function FeatureGate({
  children,
  required = [], // e.g. ["feat.branding"]
  requireActiveSub = true, // also require trialing/active/past_due
  title = "This feature is not included in your plan",
  intro, // optional sentence above bullets
}) {
  const { me, loading } = useBilling();

  if (loading) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <div className="relative">
          <div className="w-12 h-12 border-4 border-gray-200 border-t-black rounded-full animate-spin"></div>
          <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap text-sm text-gray-500 font-medium">
            Loading...
          </div>
        </div>
      </div>
    );
  }

  const planName = me?.subscription?.plan?.name || "Free";
  const hasActive = !!me?.hasActiveSubscription;
  const missing = required.filter((k) => !hasFeature(me?.features, k));
  const blockedBySub = requireActiveSub && !hasActive;
  const blockedByFeature = missing.length > 0;

  if (!blockedBySub && !blockedByFeature) {
    // Allowed -> render the page
    return <>{children}</>;
  }

  // Build a friendly message
  const reasons = [];
  if (blockedBySub) reasons.push("an active subscription");
  if (blockedByFeature) reasons.push("the required feature(s) for this page");

  return (
    <div className="min-h-[500px] flex items-center justify-center p-6">
      <div className="max-w-2xl w-full">
        {/* Decorative background gradient */}
        <div className="absolute inset-0 -z-10 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gray-100 rounded-full blur-3xl opacity-20"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-gray-100 rounded-full blur-3xl opacity-20"></div>
        </div>

        {/* Main card */}
        <div className="bg-white rounded-md shadow-xl border border-gray-100 overflow-hidden">
          {/* Header section with icon */}
          <div className="bg-gradient-to-br from-gray-50 to-gray-100 px-8 py-10 border-b border-gray-200">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-black rounded-md flex items-center justify-center shadow-lg">
                <Lock className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  {title}
                </h2>
                {intro ? (
                  <p className="text-gray-600 leading-relaxed">{intro}</p>
                ) : (
                  <p className="text-gray-600 leading-relaxed">
                    Your current plan is{" "}
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-md bg-white border border-gray-200 text-sm font-semibold text-gray-900">
                      {planName}
                    </span>
                    .<b>UPGRADE NOW FOR THIS GREAT FEATURE</b>
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Missing features section */}
          {/* {blockedByFeature && (
            <div className="px-8 py-6 bg-amber-50 border-b border-amber-100">
              <div className="flex items-start gap-3 mb-4">
                <Shield className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="text-sm font-semibold text-amber-900 mb-1">
                    Missing Features
                  </h3>
                  <p className="text-sm text-amber-700">
                    The following features are required to access this page:
                  </p>
                </div>
              </div>
              <div className="space-y-2 ml-8">
                {missing.map((k) => (
                  <div
                    key={k}
                    className="flex items-center gap-2 text-sm text-amber-900"
                  >
                    <div className="w-1.5 h-1.5 bg-amber-500 rounded-full"></div>
                    <code className="px-2 py-1 bg-white rounded-md border border-amber-200 text-amber-900 font-mono text-xs">
                      {k}
                    </code>
                  </div>
                ))}
              </div>
            </div>
          )} */}

          {/* Action buttons */}
          <div className="px-8 py-6 bg-gray-50">
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/dashboard/subscription"
                className="flex-1 group relative px-6 py-3.5 bg-black hover:bg-neutral-800 text-white rounded-md font-semibold shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center gap-2"
              >
                <span>View Plans & Upgrade</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </Link>
              <Link
                to="/dashboard"
                className="px-6 py-3.5 bg-white hover:bg-gray-50 text-gray-700 rounded-md font-semibold border-2 border-gray-200 hover:border-gray-300 transition-all duration-200 flex items-center justify-center gap-2"
              >
                <Home className="w-4 h-4" />
                <span>Back to Dashboard</span>
              </Link>
            </div>
            <p className="text-xs text-gray-500 text-center mt-4">
              Need help choosing a plan?{" "}
              <a
                href="#"
                className="text-gray-900 hover:text-gray-600 font-medium"
              >
                Contact support
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
